import SafeImage from '@ui/SafeImage';
import { slugify } from '@util/urlHelpers';
import { ProductDocument, Variation } from 'models/product';

type ProductVariantSelectorProps = {
  product: ProductDocument;
  selectedVariations: Variation[];
  onSelection: (v: Variation, isNumber: boolean, isColor: boolean) => void;
};

export default function ProductVariantSelector({
  product,
  selectedVariations,
  onSelection,
}: ProductVariantSelectorProps) {
  if (!product.variations) return;

  const isGearCombo = product.categories.some(
    (c) => c.toLowerCase() === 'gear combos'
  );

  const parsedVariations: {
    letters: Variation[];
    numbers: Variation[];
  } = {
    letters: [],
    numbers: [],
  };

  for (const variant of product.variations) {
    if (variant.is_number) {
      parsedVariations.numbers.push(variant);
    } else {
      parsedVariations.letters.push(variant);
    }
  }

  const cleanSelectedVariations = selectedVariations.reduce(
    (acc: Variation[], variation) => {
      if (variation.color && variation.color !== 'N/A') {
        const hasColor = acc.some((v) => v.color && v.color !== 'N/A');
        if (!hasColor) {
          acc.push(variation);
        }
        return acc;
      }

      if (variation.size && variation.size !== 'N/A') {
        if (variation.is_number) {
          const hasNumber = acc.some(
            (v) => v.is_number && v.size && v.size !== 'N/A'
          );
          if (!hasNumber) {
            acc.push(variation);
          }
        } else {
          const hasLetter = acc.some(
            (v) => !v.is_number && v.size && v.size !== 'N/A' && !v.color
          );
          if (!hasLetter) {
            acc.push(variation);
          }
        }
      }

      return acc;
    },
    []
  );

  return (
    <div className="flex flex-col gap-y-14">
      {product.variations.some((v) => !!v.color) && (
        <ColorSelector
          product={product}
          onSelection={onSelection}
          selectedVariations={cleanSelectedVariations}
        />
      )}

      {product.variations.some((v) => v.size !== 'N/A') && (
        <SizeSelector
          product={product}
          onSelection={onSelection}
          selectedVariations={cleanSelectedVariations}
          isGearCombo={isGearCombo}
        />
      )}
    </div>
  );
}

const sizeSort = [
  'XXXS',
  'XXS',
  'XS',
  'S',
  'SM',
  'M',
  'MD',
  'L',
  'LG',
  'XL',
  'XXL',
  '2XL',
  '3XL',
  '4XL',
  '5XL',
  '6XL',
  '7XL',
  '8XL',
  '9XL',
  '10XL',
];

const fullNameToSizeKey: Record<string, string> = {
  'XXX-SMALL': 'XXXS',
  'XX-SMALL': 'XXS',
  'X-SMALL': 'XS',
  'EXTRA SMALL': 'XS',
  SMALL: 'S',
  MEDIUM: 'M',
  LARGE: 'L',
  'X-LARGE': 'XL',
  'EXTRA LARGE': 'XL',
  'XX-LARGE': 'XXL',
  'XXX-LARGE': '3XL',
};

const getFirstSizeKey = (size: string) => {
  const trimmed = size.trim();
  const mapped = fullNameToSizeKey[trimmed.toUpperCase()];
  if (mapped) return mapped;
  return trimmed.split('-')[0].trim();
};

const SizeSelector = ({
  product,
  onSelection,
  selectedVariations,
  isGearCombo,
}: {
  product: ProductDocument;
  onSelection: (v: Variation, isNumber: boolean, isColor: boolean) => void;
  selectedVariations: Variation[];
  isGearCombo: boolean;
}) => {
  const uniqueSizes = Array.from(
    new Set(
      product.variations?.map((v) => v.size).filter((size) => size !== 'N/A')
    )
  ).sort((a, b) => {
    if (typeof a === 'string' && typeof b === 'string') {
      const aKey = getFirstSizeKey(a);
      const bKey = getFirstSizeKey(b);
      const aIdx = sizeSort.indexOf(aKey);
      const bIdx = sizeSort.indexOf(bKey);

      if (aIdx !== -1 && bIdx !== -1) {
        return aIdx - bIdx;
      }
      return a.localeCompare(b);
    } else if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    } else {
      return a.toString().localeCompare(b.toString());
    }
  });

  return (
    <div className="flex flex-col gap-y-6 pt-6">
      {selectedVariations.length ? (
        <>
          {isGearCombo ? (
            <div className="flex flex-col gap-y-2 text-[1.8rem] sm:text-[2.2rem] md:text-[1.8rem] ">
              <div className="flex items-center gap-2">
                <span className="font-medium text-black">Jersey Size: </span>{' '}
                {selectedVariations
                  .filter((v) => !v.is_number)
                  .map((v) => (
                    <div key={v.size}>{v.size}</div>
                  ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium text-black">Pant Size: </span>{' '}
                {selectedVariations
                  .filter((v) => v.is_number)
                  .map((v) => (
                    <div key={v.size}>{v.size}</div>
                  ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-x-2 text-[1.8rem] sm:text-[2.2rem] md:text-[1.8rem] ">
              <span className="font-medium text-black">Size: </span>{' '}
              {selectedVariations.map((v) => (
                <div key={v.size}>{v.size}</div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {!isGearCombo && (
            <div className="flex flex-col gap-y-2">
              <span className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
                Select a size
              </span>
            </div>
          )}
        </>
      )}

      {isGearCombo ? (
        <div className="flex flex-col gap-y-8">
          <span className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
            Select a Jersey size
          </span>

          <div className="flex flex-wrap gap-4">
            {uniqueSizes
              .filter((size) => typeof size === 'string')
              .map((size) => (
                <SizeSelectButton
                  key={size}
                  selectedVariations={selectedVariations}
                  size={size}
                  product={product}
                  onSelection={onSelection}
                />
              ))}
          </div>

          <span className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
            Select a Pant size
          </span>

          <div className="flex flex-wrap gap-4">
            {uniqueSizes
              .filter((size) => typeof size === 'number')
              .map((size) => (
                <SizeSelectButton
                  key={size}
                  selectedVariations={selectedVariations}
                  size={size}
                  product={product}
                  onSelection={onSelection}
                />
              ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-4">
          {uniqueSizes.map((size) => (
            <SizeSelectButton
              key={size}
              selectedVariations={selectedVariations}
              size={size}
              product={product}
              onSelection={onSelection}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SizeSelectButton = ({
  selectedVariations,
  size,
  product,
  onSelection,
}: {
  selectedVariations: Variation[];
  size: string | number;
  product: ProductDocument;
  onSelection: (v: Variation, isNumber: boolean, isColor: boolean) => void;
}) => {
  const currentColor = selectedVariations.find((v) => !!v.color)?.color;

  let variation = product.variations?.find((v) => v.size === size);

  if (currentColor) {
    const variationWithSameColor = product.variations?.find(
      (v) => v.size === size && v.color === currentColor
    );
    if (variationWithSameColor) {
      variation = variationWithSameColor;
    }
  }

  if (!variation) return null;

  const isSizeUnavailable = currentColor
    ? !product.variations?.some(
        (v) => v.size === size && v.color === currentColor && v.qty > 0
      )
    : !product.variations?.some((v) => v.size === size && v.qty > 0);

  if (!variation) return null;
  const isSelected = selectedVariations.some((v) => v.size === size);

  return (
    <button
      type="button"
      onClick={() => {
        onSelection(variation, variation.is_number, false);
      }}
      className={`relative flex h-[6rem] w-fit min-w-[10rem] items-center justify-center rounded-[1rem] border px-2 md:h-[5rem] md:min-w-[8rem] ${
        isSelected ? 'border-brand-secondary' : 'border-zinc-200'
      } ${
        isSizeUnavailable
          ? 'bg-gray-200 opacity-40 before:absolute before:left-1/2 before:top-1/2 before:z-10 before:h-[2px] before:w-[90%] before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:bg-red-500/70 before:content-[""]'
          : 'cursor-pointer hover:border-brand-secondary'
      } overflow-hidden font-medium text-zinc-800 transition-colors duration-150`}
      disabled={isSizeUnavailable}
      key={size}
    >
      {size}
    </button>
  );
};

const ColorSelector = ({
  product,
  onSelection,
  selectedVariations,
}: {
  product: ProductDocument;
  onSelection: (v: Variation, isNumber: boolean, isColor: boolean) => void;
  selectedVariations: Variation[];
}) => {
  const imageToColorMap = new Map<string, string>();
  product.variations?.forEach((v) => {
    if (v.color && v.color !== 'N/A') {
      imageToColorMap.set(v.image ?? 'no-image-' + slugify(v.color), v.color);
    }
  });

  const uniqueImages = Array.from(imageToColorMap.keys());

  const selectedSize = selectedVariations.length
    ? selectedVariations[0].size
    : undefined;

  return (
    <div className="flex flex-col gap-y-6">
      {selectedVariations.length ? (
        <div className="flex items-center gap-x-2 text-[1.8rem] sm:text-[2.2rem] md:text-[1.8rem]">
          <span className="font-medium text-black">Color: </span>{' '}
          {selectedVariations.map((v) => (
            <div key={v.color}>{v.color}</div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-y-2">
          <span className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
            Select a color
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {uniqueImages.map((image) => {
          const color = imageToColorMap.get(image);
          const variantWithColor = product.variations?.find(
            (v) => !!v && v.color === color
          );
          const isColorUnavailable = product.variations
            ?.filter((v) => v.color === color)
            .every((v) => v.qty === 0);

          let optimizedImage = image;
          if (image.startsWith('https://cdn.wpsstatic.com/images/')) {
            optimizedImage = image.replace(/\/(500|1000)_max\//g, '/200_max/');
          }

          // const isColorUnavailable = selectedSize
          //   ? !product.variations?.some(
          //       (v) => v.size === selectedSize && v.color === color && v.qty > 0
          //     )
          //   : !product.variations?.some((v) => v.color === color && v.qty > 0);

          if (variantWithColor) {
            const isSelected = selectedVariations.some(
              (v) => v.color === color
            );
            return (
              <button
                type="button"
                className={`relative flex h-[8rem] w-fit min-w-[8rem] flex-col items-center justify-center rounded-[1rem] border p-2 transition-colors duration-150 md:h-[6rem]  ${
                  isSelected ? 'border-brand-secondary' : 'border-zinc-200'
                } ${
                  isColorUnavailable
                    ? 'opacity-40'
                    : 'hover:border-brand-secondary'
                }`}
                key={`image-${image}`}
                disabled={isColorUnavailable}
                onClick={() => {
                  const currentSizeInStock = product.variations?.find(
                    (v) =>
                      v.color === color && v.size === selectedSize && v.qty > 0
                  );

                  if (currentSizeInStock) {
                    onSelection(currentSizeInStock, false, true);
                  } else if (selectedSize) {
                    const sizesInStock = product.variations?.filter(
                      (v) => v.color === color && v.qty > 0
                    );
                    const closestSizeInStock = sizesInStock?.reduce(
                      (prev, curr) => {
                        return Math.abs(
                          Number(curr.size) - Number(selectedSize)
                        ) < Math.abs(Number(prev.size) - Number(selectedSize))
                          ? curr
                          : prev;
                      },
                      sizesInStock[0]
                    );
                    if (closestSizeInStock) {
                      onSelection(closestSizeInStock, false, true);
                    }
                  } else {
                    const firstSizeInStock = product.variations?.find(
                      (v) => v.color === color && v.qty > 0
                    );
                    if (firstSizeInStock) {
                      onSelection(firstSizeInStock, false, true);
                    }
                  }
                }}
                title={color}
              >
                {image.includes('no-image-') ? (
                  <span className="flex h-full w-full items-center justify-center text-center">
                    {color}
                  </span>
                ) : (
                  <SafeImage
                    src={optimizedImage}
                    alt={`${color} ${product.title}`}
                    className={`mx-auto h-full w-[7rem] rounded-[1rem] object-contain`}
                    height={100}
                    width={100}
                  />
                )}
              </button>
            );
          }
        })}
      </div>
    </div>
  );
};
